
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import DashboardLayout from "@/components/DashboardLayout";
import PasswordChangeForm from "@/components/PasswordChangeForm";
import TwoFactorAuth from "@/components/TwoFactorAuth";
import { User, Mail, Crown, Camera, Save, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      updateUser({ name: formData.name, email: formData.email });
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setIsUpdating(false);
    }
  };

  const planDetails = {
    free: { name: 'Free', color: 'bg-gradient-to-r from-gray-400 to-gray-500', limit: 5, features: ['Basic AI Tools', 'Standard Quality', 'Community Support'] },
    pro: { name: 'Pro', color: 'bg-genova-gradient', limit: 'Unlimited', features: ['All AI Tools', 'HD Quality', 'Priority Support', 'Advanced Templates'] },
    team: { name: 'Team', color: 'bg-gradient-to-r from-purple-600 to-pink-600', limit: 'Unlimited', features: ['Everything in Pro', 'Team Collaboration', 'Custom Branding', 'Dedicated Support'] }
  };

  const currentPlan = user ? planDetails[user.plan as keyof typeof planDetails] : planDetails.free;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-8 p-6">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <Sparkles className="w-8 h-8 text-genova-purple animate-pulse" />
            <h1 className="text-4xl font-bold bg-genova-gradient bg-clip-text text-transparent">
              Profile Settings
            </h1>
          </div>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Manage your account settings, security preferences, and personalize your Genova AI experience
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Profile Information */}
            <Card className="genova-card border-0 shadow-2xl">
              <CardHeader className="pb-6">
                <CardTitle className="flex items-center space-x-3">
                  <User className="w-6 h-6 text-genova-purple" />
                  <span className="text-xl">Personal Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
                    <div className="relative group">
                      <Avatar className="w-24 h-24 border-4 border-genova-purple/20 shadow-lg">
                        <AvatarImage src={user?.avatarUrl} />
                        <AvatarFallback className="bg-genova-gradient text-white text-2xl font-bold">
                          {user?.name?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <Button
                        type="button"
                        size="icon"
                        className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-genova-gradient shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-110"
                      >
                        <Camera className="w-5 h-5" />
                      </Button>
                    </div>
                    <div className="text-center md:text-left">
                      <h3 className="text-2xl font-bold">{user?.name}</h3>
                      <p className="text-muted-foreground mb-3">{user?.email}</p>
                      <Badge className={`${currentPlan.color} text-white px-4 py-2 text-sm font-medium shadow-lg`}>
                        {user?.plan === 'team' && <Crown className="w-4 h-4 mr-2" />}
                        {currentPlan.name} Member
                      </Badge>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="genova-input h-12"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="genova-input h-12"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <Button type="submit" disabled={isUpdating} className="genova-button h-12 px-8">
                    <Save className="w-5 h-5 mr-2" />
                    {isUpdating ? 'Updating...' : 'Save Changes'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Security Section */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold flex items-center space-x-3">
                <span className="w-8 h-8 bg-genova-gradient rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 text-white" />
                </span>
                <span>Security & Privacy</span>
              </h2>
              
              <div className="grid gap-6">
                <PasswordChangeForm />
                <TwoFactorAuth />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Enhanced Plan Details */}
            <Card className="genova-card border-0 shadow-2xl overflow-hidden">
              <div className={`h-2 ${currentPlan.color}`}></div>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center space-x-3">
                  <Crown className="w-6 h-6 text-genova-purple" />
                  <span>Current Plan</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center space-y-4">
                  <Badge className={`${currentPlan.color} text-white text-lg px-6 py-3 font-bold shadow-lg`}>
                    {currentPlan.name}
                  </Badge>
                  <div>
                    <p className="text-3xl font-bold">
                      {user?.plan === 'free' ? '₹0' : user?.plan === 'pro' ? '₹499' : '₹1,499'}
                      <span className="text-sm font-normal text-muted-foreground">/month</span>
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-accent/50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Monthly Generations</span>
                      <span className="font-bold text-genova-purple">{currentPlan.limit}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Used This Month</span>
                      <span className="font-bold">{user?.creditsUsed || 0}</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Plan Features</h4>
                    {currentPlan.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-genova-gradient rounded-full"></div>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {user?.plan === 'free' && (
                  <Button className="w-full genova-button h-12 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                    <Crown className="w-5 h-5 mr-2" />
                    Upgrade Now
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Enhanced Usage Stats */}
            <Card className="genova-card border-0 shadow-2xl">
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <Zap className="w-6 h-6 text-genova-blue" />
                  <span>Activity Overview</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border">
                    <div className="text-2xl font-bold text-genova-purple">12</div>
                    <div className="text-xs text-muted-foreground">Images</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-lg border">
                    <div className="text-2xl font-bold text-genova-teal">5</div>
                    <div className="text-xs text-muted-foreground">Scripts</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-teal-50 to-green-50 rounded-lg border">
                    <div className="text-2xl font-bold text-green-600">3</div>
                    <div className="text-xs text-muted-foreground">Voices</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span>Credits Remaining</span>
                    <span className="font-bold text-genova-purple">
                      {user?.plan === 'free' ? 
                        `${(user?.creditsLimit || 5) - (user?.creditsUsed || 0)}` : 
                        '∞'
                      }
                    </span>
                  </div>
                  {user?.plan === 'free' && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-genova-gradient h-2 rounded-full transition-all duration-300" 
                        style={{ 
                          width: `${Math.min(((user?.creditsUsed || 0) / (user?.creditsLimit || 5)) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
